(() => {
  const aliases = {
    black: ['black', 'bk', 'blk'], white: ['white', 'wh', 'wt', 'wht'], blue: ['blue', 'bu', 'bl', 'blu'],
    red: ['red', 'rd', 're'], pink: ['pink', 'pk', 'pn', 'rose', 'rs', 'mr'],
    green: ['green', 'gn', 'grn'], grey: ['grey', 'gray', 'gy', 'gr', 'gd'],
    yellow: ['yellow', 'yl', 'yel'], orange: ['orange', 'og'], purple: ['purple', 'pr', 'pp', 'pl'],
    brown: ['brown', 'br'], beige: ['beige', 'be'], gold: ['gold', 'gd'],
    silver: ['silver', 'sl'], navy: ['navy', 'nv']
  };

  const normal = value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const mediaKey = element => {
    const alt = element.querySelector('img')?.alt || '';
    // Existing catalog images use several alt formats. Keep every view of a
    // colour under the same key, including imported filename based images.
    const named = alt.match(/(?:^|\s)([A-Z0-9]+(?:-[A-Z0-9]+)*)\s+([A-Z]{2,4})\s+[^\s]+\.(?:jpe?g|png|webp)$/i);
    if (named) return named[2].toLowerCase();
    const views = alt.match(/-([A-Z]{2,4})\s+view\s+\d+$/i);
    if (views) return views[1].toLowerCase();
    const pieces = alt.split(/\s+-\s+/);
    const group = pieces.length >= 3 ? pieces[pieces.length - 2] : pieces[pieces.length - 1];
    const words = normal(group).split(' ');
    return words[words.length - 1] || '';
  };
  const imageSrc = element => {
    const img = element?.querySelector('img');
    if (!img) return '';
    const url = img.currentSrc || img.src;
    return url ? url.replace(/([?&])width=\d+/, '$1width=180') : '';
  };

  const canonical = value => {
    const key = normal(value);
    return Object.keys(aliases).find(name => aliases[name].includes(key)) || key;
  };
  const colorParts = value => normal(value).split(' ').filter(Boolean).map(canonical);
  const groupParts = value => {
    const key = normal(value).replace(/ /g, '');
    const whole = canonical(key);
    if (whole !== key) return [whole];
    const codes = Object.values(aliases).flat().filter(code => code.length > 1 && code !== key)
      .sort((left, right) => right.length - left.length);
    const parts = [];
    let rest = key;
    while (rest) {
      const code = codes.find(item => rest.startsWith(item));
      if (!code) return [key];
      parts.push(canonical(code));
      rest = rest.slice(code.length);
    }
    return parts;
  };
  const sameParts = (left, right) => left.length === right.length &&
    left.every(part => right.includes(part));

  function assignGroups(colors, groups, inputs = [], grid = []) {
    const result = new Map();
    const used = new Set();
    // A Shopify variant image is the strongest available colour-to-photo link.
    for (const input of inputs) {
      const file = input.nextElementSibling?.querySelector('.swatch')?.dataset.variantImg?.split('/').pop()?.split('?')[0];
      if (!file) continue;
      const image = grid.find(item => item.querySelector('img')?.src.split('/').pop()?.split('?')[0] === file);
      const group = image && mediaKey(image);
      if (group && groups.includes(group) && !used.has(group)) {
        result.set(input.value, group);
        used.add(group);
      }
    }
    // Match a complete colour name or abbreviation before using a component.
    for (const color of colors.filter(value => !result.has(value))) {
      const choices = aliases[normal(color)] || [normal(color)];
      const matches = groups.filter(group => !used.has(group) && choices.includes(group));
      if (matches.length === 1) { result.set(color, matches[0]); used.add(matches[0]); }
    }
    // Composite labels such as Black/Red can match BKRD without relying on order.
    for (const color of colors.filter(value => !result.has(value))) {
      const parts = colorParts(color);
      const matches = groups.filter(group => !used.has(group) && sameParts(parts, groupParts(group)));
      if (matches.length === 1) { result.set(color, matches[0]); used.add(matches[0]); }
    }
    // A single colour can also match a two-colour image code, such as Red -> BKRD.
    // Only accept it when no other remaining colour could claim that image group.
    for (const color of colors.filter(value => !result.has(value))) {
      const parts = colorParts(color);
      const matches = groups.filter(group => !used.has(group) &&
        parts.some(part => groupParts(group).includes(part)));
      if (matches.length !== 1) continue;
      const competitors = colors.filter(other => !result.has(other) && other !== color &&
        colorParts(other).some(part => groupParts(matches[0]).includes(part)));
      if (competitors.length === 0) { result.set(color, matches[0]); used.add(matches[0]); }
    }
    const missingColors = colors.filter(color => !result.has(color));
    const missingGroups = groups.filter(group => !used.has(group));
    if (missingColors.length === 1 && missingGroups.length === 1) result.set(missingColors[0], missingGroups[0]);
    return result;
  }

  function setup(productInfo) {
    if (!productInfo.aorakiColorDelegated) {
      productInfo.addEventListener('change', event => {
        if (event.target.matches('variant-selects input[type="radio"]')) {
          window.setTimeout(() => setup(productInfo), 350);
        }
      });
      productInfo.aorakiColorDelegated = true;
    }
    const gallery = productInfo.querySelector('media-gallery');
    const colorField = [...productInfo.querySelectorAll('variant-selects fieldset')]
      .find(field => /\bcolou?r\b/i.test(field.querySelector('legend')?.textContent || ''));
    if (!gallery || !colorField) return;
    const inputs = [...colorField.querySelectorAll('input[type="radio"]')];
    const grid = [...gallery.querySelectorAll('.media-gallery__grid > li')];
    if (inputs.length < 2 || !grid.length) return;
    const groups = [...new Set(grid.map(mediaKey).filter(Boolean))];
    if (groups.length < 2) return;
    const assigned = assignGroups(inputs.map(input => input.value), groups, inputs, grid);
    const valid = [...assigned.values()].filter((group, index, all) => all.indexOf(group) === index);
    if (valid.length < 2) return;

    const main = [...gallery.querySelectorAll('.swiper-main .swiper-wrapper > .swiper-slide')];
    const thumbs = [...gallery.querySelectorAll('.swiper-controls__thumbnails .swiper-wrapper > .swiper-slide')];
    const all = [...grid, ...main, ...thumbs];
    for (const input of inputs) {
      const group = assigned.get(input.value);
      const swatch = input.nextElementSibling?.querySelector('.swatch');
      const hero = grid.find(item => mediaKey(item) === group);
      const src = imageSrc(hero);
      if (swatch && src) {
        swatch.style.setProperty('--aoraki-swatch-image', `url("${src}")`);
        swatch.classList.add('aoraki-color-picture');
      }
    }

    const apply = () => {
      const selected = inputs.find(input => input.checked)?.value;
      const selectedGroup = assigned.get(selected);
      if (!selectedGroup) return;
      for (const item of all) {
        const key = mediaKey(item);
        const visible = !key || key === selectedGroup;
        item.classList.toggle('aoraki-other-color', !visible);
        item.setAttribute('aria-hidden', String(!visible));
      }
      const first = grid.find(item => mediaKey(item) === selectedGroup);
      const mediaId = first?.querySelector('[data-media-id]')?.dataset.mediaId;
      if (mediaId) gallery.setActiveMedia?.(mediaId, false);
      const swiperComponent = gallery.querySelector('swiper-component');
      swiperComponent?.initSwiper?.update?.();
      swiperComponent?.initSwiper?.slideTo?.(0, 0);
      swiperComponent?.thumbnailSwiper?.update?.();
    };

    if (productInfo.aorakiColorCleanup) productInfo.aorakiColorCleanup();
    colorField.addEventListener('change', apply);
    productInfo.aorakiColorCleanup = () => colorField.removeEventListener('change', apply);
    apply();
  }

  const init = () => document.querySelectorAll('.main-product-information product-info').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('product-info:loaded', init);
  document.addEventListener('change', event => {
    if (!event.target.matches('.main-product-information variant-selects input[type="radio"]')) return;
    window.setTimeout(init, 0);
    window.setTimeout(init, 500);
  }, true);
  const observer = new MutationObserver(mutations => {
    if (mutations.some(mutation => [...mutation.addedNodes].some(node =>
      node.nodeType === 1 && (node.matches?.('product-info, variant-selects, media-gallery') || node.querySelector?.('product-info, variant-selects, media-gallery'))
    ))) init();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();



